import { useState, useEffect, useRef } from "react";
import { Code, Database, Cloud, Cpu, Layers, Lock } from "lucide-react";
import logo from "@/public/images/logo.png";
import Image from "next/image";

const OrbitalAnimation = () => {
  const [rotationAngle, setRotationAngle] = useState(0);
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);
  const [autoRotate, setAutoRotate] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
  
    handleResize();
  
    window.addEventListener("resize", handleResize);
  
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nodes = [
    {
      id: 1,
      title: "Scalable",
      icon: Layers,
      description: "Architecture that grows with your business",
    },
    {
      id: 2,
      title: "Secure",
      icon: Lock,
      description: "Enterprise-grade security by default",
    },
    {
      id: 3,
      title: "AI-Ready",
      icon: Cpu,
      description: "Built for intelligent automation",
    },
    {
      id: 4,
      title: "Cloud",
      icon: Cloud,
      description: "Cloud-native infrastructure",
    },
    {
      id: 5,
      title: "Data",
      icon: Database,
      description: "Data integrity and performance",
    },
    {
      id: 6,
      title: "Code",
      icon: Code,
      description: "Clean, maintainable codebase",
    },
  ];

  useEffect(() => {
    let rotationTimer: ReturnType<typeof setInterval>;

    if (autoRotate) {
      rotationTimer = setInterval(() => {
        setRotationAngle((prev) => {
          const newAngle = (prev + 0.3) % 360;
          return Number(newAngle.toFixed(3));
        });
      }, 50);
    }

    return () => {
      if (rotationTimer) {
        clearInterval(rotationTimer);
      }
    };
  }, [autoRotate]);

  const calculateNodePosition = (index: any, total: any) => {
    const angle = ((index / total) * 360 + rotationAngle) % 360;
    const radius = isMobile ? 100 : 180;
    const radian = (angle * Math.PI) / 180;

    const x = radius * Math.cos(radian);
    const y = radius * Math.sin(radian);

    const zIndex = Math.round(100 + 50 * Math.cos(radian));
    const opacity = Math.max(
      0.5,
      Math.min(1, 0.5 + 0.5 * ((1 + Math.sin(radian)) / 2))
    );
    const scale = 0.7 + 0.3 * ((1 + Math.sin(radian)) / 2);

    return { x, y, angle, zIndex, opacity, scale };
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Central Hub */}
      <div className="absolute">
        <div className={`relative ${isMobile ? 'w-14 h-14 p-2' : 'w-24 h-24 p-4'} rounded-full bg-white flex items-center justify-center`} style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)' }}>
          {/* Pulse rings - grey tones */}
          <div className={`absolute ${isMobile ? 'w-16 h-16' : 'w-28 h-28'} rounded-full border border-[#9CA3AF]/30 animate-ping opacity-70`}></div>
          <div
            className={`absolute ${isMobile ? 'w-20 h-20' : 'w-32 h-32'} rounded-full border border-[#9CA3AF]/20 animate-ping opacity-50`}
            style={{ animationDelay: "0.5s" }}
          ></div>

          <Image src={logo} alt="ZySoftec" className="w-full h-full object-contain z-10 relative" />
        </div>
      </div>

      {/* Orbit ring */}
      <div className={`absolute ${isMobile ? 'w-52 h-52' : 'w-96 h-96'} rounded-full border border-[#E5E7EB]`}></div>

      {/* Orbiting nodes */}
      {nodes.map((node, index) => {
        const position = calculateNodePosition(index, nodes.length);
        const isHovered = hoveredNode === node.id;
        const Icon = node.icon;

        return (
          <div
            key={node.id}
            className="absolute transition-all duration-700 cursor-pointer"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${
                isHovered ? 1.2 : position.scale
              })`,
              zIndex: isHovered ? 200 : position.zIndex,
              opacity: isHovered ? 1 : position.opacity,
            }}
            onMouseEnter={() => {
              setHoveredNode(node.id);
              setAutoRotate(false);
            }}
            onMouseLeave={() => {
              setHoveredNode(null);
              setAutoRotate(true);
            }}
          >
            {/* Glow effect - subtle grey/amber on hover */}
            <div
              className={`absolute rounded-full -inset-1 ${
                isHovered ? "animate-pulse" : ""
              }`}
              style={{
                background: isHovered
                  ? `radial-gradient(circle, rgba(122, 111, 240, 0.2) 0%, rgba(122, 111, 240, 0) 70%)`
                  : `radial-gradient(circle, rgba(156, 163, 175, 0.15) 0%, rgba(156, 163, 175, 0) 70%)`,
                width: isMobile ? "50px" : "80px",
                height: isMobile ? "50px" : "80px",
                left: isMobile ? "-8px" : "-15px",
                top: isMobile ? "-8px" : "-15px",
              }}
            ></div>

            {/* Node */}
            <div
              className={`${isMobile ? 'w-9 h-9' : 'w-14 h-14'} rounded-lg flex items-center justify-center transition-all duration-300 mx-auto`}
              style={{
                backgroundColor: isHovered ? '#7A6FF0' : '#FFFFFF',
                color: isHovered ? '#FFFFFF' : '#4B5563',
                boxShadow: isHovered
                  ? '0 4px 16px rgba(122, 111, 240, 0.25)'
                  : '0 2px 8px rgba(0, 0, 0, 0.05)',
                border: '1px solid #E5E7EB',
              }}
            >
              <Icon className={isMobile ? 'w-4 h-4' : 'w-7 h-7'} />
            </div>

            {/* Title */}
            <div
              className={`absolute ${isMobile ? '-bottom-5 text-[9px]' : '-bottom-8 text-[13px]'} left-1/2 whitespace-nowrap text-center transition-all duration-300`}
              style={{
                fontWeight: 500,
                color: isHovered ? '#7A6FF0' : '#4B5563',
                transform: isHovered ? 'translateX(-50%) scale(1.1)' : 'translateX(-50%) scale(1)',
              }}
            >
              {node.title}
            </div>

            {/* Hover tooltip */}
            {isHovered && (
              <div
                className={`absolute ${isMobile ? 'bottom-full mb-8 text-[10px] px-2 py-1.5 max-w-[140px]' : 'top-full mt-12 text-[13px] px-3 py-2 whitespace-nowrap'} left-1/2 -translate-x-1/2 rounded-lg z-[999] text-center`}
                style={{
                  backgroundColor: '#FFFFFF',
                  color: '#4B5563',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  border: '1px solid #E5E7EB',
                  fontWeight: 400,
                }}
              >
                <div
                  className={`absolute left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 ${isMobile ? '-bottom-1.5' : '-top-2'}`}
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderLeft: isMobile ? 'none' : '1px solid #E5E7EB',
                    borderTop: isMobile ? 'none' : '1px solid #E5E7EB',
                    borderRight: isMobile ? '1px solid #E5E7EB' : 'none',
                    borderBottom: isMobile ? '1px solid #E5E7EB' : 'none',
                  }}
                ></div>
                <p className="m-0">{node.description}</p>
              </div>
            )}
          </div>
        );
      })}

      {/* Connection lines (subtle grey) */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-10">
        {nodes.map((_, index) => {
          const pos1 = calculateNodePosition(index, nodes.length);
          return (
            <line
              key={index}
              x1="50%"
              y1="50%"
              x2={`calc(50% + ${pos1.x}px)`}
              y2={`calc(50% + ${pos1.y}px)`}
              stroke="#9CA3AF"
              strokeWidth="1"
              opacity={pos1.opacity}
            />
          );
        })}
      </svg>
    </div>
  );
};

export default OrbitalAnimation;
